/**
 * Reading a chosen CSV file for the import modals (DESIGN_EDITING.md console flows 3 and 9). The whole file's bytes are
 * kept for the upload and its hash; only the first 64 KiB is decoded, for the delimiter, header, preview and types.
 * This one touches the browser's File and TextDecoder, so the pure parsing in csv.ts stays testable on its own.
 */

export const PREVIEW_BYTES = 64 * 1024;

export interface LoadedCsv {
    fileName: string;
    size: number;
    /** The whole file, uploaded in chunks and hashed; the helper strips a BOM on commit. */
    bytes: Uint8Array;
    /** The first 64 KiB decoded as UTF-8, or null when the bytes are not UTF-8 ("Not UTF-8."). */
    text: string | null;
}

/** Read a file into bytes and a decoded preview. A sequence cut at the 64 KiB edge is tolerated, not called invalid. */
export const readCsvFile = async (file: File): Promise<LoadedCsv> => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let text: string | null = null;
    try {
        const decoder = new TextDecoder('utf-8', { fatal: true });
        text = decoder.decode(bytes.subarray(0, PREVIEW_BYTES), { stream: bytes.length > PREVIEW_BYTES });
    } catch {
        text = null;   // invalid UTF-8 somewhere in the preview
    }
    return { fileName: file.name, size: file.size, bytes, text };
};
